import { Descendant, Editor, Transforms } from 'slate';
import { jsx } from 'slate-hyperscript';

import { SlatePlugin } from '@nucleus/lib-slate';

import { collapseWhiteSpace } from './html/collapse-white-space';
import { copyBlockMarksToSpanChild } from './html/copy-block-marks-to-span-child';
import { htmlStringToDomNode } from './html/html-string-to-dom-node';
import { isHtmlElement } from './html/is-html-element';
import { isHtmlText } from './html/is-html-text';
import { shouldBrBecomeEmptyParagraph } from './html/should-br-become-empty-paragraph';

/** Minimal interface for the DOM-editor paste methods added by slate-react. */
interface ClipboardEditor {
  insertData: (data: DataTransfer) => void;
  insertTextData: (data: DataTransfer) => boolean;
}

/**
 * HTML elements whose entire subtree should be silently dropped during
 * deserialization. These elements contain code or metadata rather than
 * user-visible text. Apple Pages, for example, embeds a `<style>` block
 * carrying CSS class rules in its clipboard HTML.
 */
const NonContentNodeNames = new Set(['STYLE', 'SCRIPT', 'HEAD', 'META', 'LINK', 'NOSCRIPT', 'TEMPLATE']);

export type Mark = Record<string, true>;

export interface PasteHtmlRule {
  /** Element type to emit (mutually exclusive with `mark`). */
  element?: string | ((element: HTMLElement) => Record<string, unknown>);
  /** Mark key(s) to apply to descendant text (mutually exclusive with `element`). */
  mark?: string | Mark | ((element: HTMLElement) => Mark);
  /**
   * Custom deserializer; receives parsed children. When defined, wins over
   * `element` and `mark`.
   */
  custom?: (element: HTMLElement, children: Array<unknown>) => unknown;

  validNodeName?: string | Array<string>;
  validStyle?: Record<string, string | Array<string>>;
  validClassName?: string;
  /** Predicate; rule only applies when this returns true. Useful with `hasMatchingHtmlElement`. */
  query?: (element: HTMLElement) => boolean;
}

export interface PasteHtmlConfig {
  rules: Array<PasteHtmlRule>;
  /** Default element type used when top-level text/inline content has no block wrapper. */
  defaultElementType: string;
  /**
   * Data attribute names (without `data-` prefix) that mark subtrees to skip
   * during deserialization. Mirrors Plate's `data-plate-prevent-deserialization`.
   */
  preventDeserializationAttributes?: Array<string>;
}

// ---------------------------------------------------------------------------
// Rule matching
// ---------------------------------------------------------------------------

const matchesNodeName = (element: HTMLElement, validNodeName: string | Array<string>): boolean => {
  const validNames = Array.isArray(validNodeName) ? validNodeName : [validNodeName];

  return validNames.includes(element.nodeName);
};

const matchesStyle = (element: HTMLElement, validStyle: Record<string, string | Array<string>>): boolean => {
  const styleRecord = element.style as unknown as Record<string, string>;

  for (const [property, value] of Object.entries(validStyle)) {
    const elementValue = styleRecord[property] ?? '';
    const validValues = Array.isArray(value) ? value : [value];

    if (value !== '*' && !validValues.includes(elementValue)) {
      return false;
    }
    if (value === '*' && !elementValue) {
      return false;
    }
  }

  return true;
};

const ruleMatches = (rule: PasteHtmlRule, element: HTMLElement): boolean => {
  if (rule.validNodeName !== undefined) {
    if (!matchesNodeName(element, rule.validNodeName)) {
      return false;
    }
  }
  if (rule.validStyle !== undefined) {
    if (!matchesStyle(element, rule.validStyle)) {
      return false;
    }
  }
  if (rule.validClassName !== undefined) {
    if (!element.classList.contains(rule.validClassName)) {
      return false;
    }
  }
  if (rule.query !== undefined) {
    if (!rule.query(element)) {
      return false;
    }
  }

  return true;
};

// ---------------------------------------------------------------------------
// Deserializer
// ---------------------------------------------------------------------------

type DeserializedNode = string | Descendant | Array<DeserializedNode> | null;

const buildDeserializer = (config: PasteHtmlConfig): ((node: ChildNode) => DeserializedNode) => {
  const { defaultElementType, preventDeserializationAttributes = [], rules } = config;

  const deserializeNode = (node: ChildNode): DeserializedNode => {
    // Text nodes
    if (isHtmlText(node)) {
      const parent = node.parentElement;

      if (parent !== null) {
        for (const attr of preventDeserializationAttributes) {
          if (parent.dataset[attr] !== undefined) {
            return '';
          }
        }
      }

      return node.textContent ?? '';
    }

    if (!isHtmlElement(node)) {
      return null;
    }

    const element = node as HTMLElement;

    // Elements that should never contribute content — skip entire subtree.
    // <style> and <script> contain raw code, not document text. <meta>, <link>,
    // <head>, and <noscript> are document infrastructure with no paste value.
    // Apple Pages embeds a <style> block in clipboard HTML containing CSS class
    // rules; without this guard the raw CSS string appears as pasted text.
    if (NonContentNodeNames.has(element.nodeName)) {
      return null;
    }

    // Skip nodes marked to prevent deserialization
    for (const attr of preventDeserializationAttributes) {
      if (element.dataset[attr] !== undefined) {
        return null;
      }
    }

    // Apple Safari copy artifact — skip entirely
    if (element.nodeName === 'BR' && element.className === 'Apple-interchange-newline') {
      return null;
    }

    // BR handling
    if (element.nodeName === 'BR') {
      if (shouldBrBecomeEmptyParagraph(element)) {
        return jsx('element', { type: defaultElementType }, [{ text: '' }]) as unknown as Descendant;
      }

      // Return an object leaf rather than a plain string so that
      // slate-hyperscript does not merge it into adjacent text. Keeping it
      // as its own {text:'\n'} leaf means the HTML serializer can convert it
      // back to <br /> on copy, preserving the round-trip.
      return { text: '\n' } as unknown as Descendant;
    }

    // BODY — return a fragment
    if (element.nodeName === 'BODY') {
      const children = deserializeChildren(element, deserializeNode);

      return jsx('fragment', {}, children) as unknown as Descendant;
    }

    // Collect deserialized children for use in rules
    const children = deserializeChildren(element, deserializeNode);

    // Find element rules first (they build block/inline nodes)
    for (const rule of rules) {
      if (!ruleMatches(rule, element)) {
        continue;
      }

      // Custom deserializer wins
      if (rule.custom !== undefined) {
        const result = rule.custom(element, children);

        if (result !== undefined) {
          // Normalize element-like results: raw strings in children fail Slate's
          // Node.isNodeList check (required by Element.isElement in v0.103+).
          // Convert any string child to a proper {text} leaf.
          const customResult = result as Record<string, unknown>;

          if (Array.isArray(customResult['children'])) {
            const normalizedChildren = (customResult['children'] as Array<unknown>).map((child) =>
              typeof child === 'string' ? { text: child } : child
            );

            return { ...customResult, children: normalizedChildren } as Descendant;
          }

          return result as Descendant;
        }
      }

      // Element rule
      if (rule.element !== undefined) {
        const attrs = typeof rule.element === 'function' ? rule.element(element) : { type: rule.element };

        // When a block element contains only <br> tags its children will be
        // {text:'\n'} leaves with no real text content. Treat this as an empty
        // paragraph rather than a paragraph with a literal newline, which would
        // render as an extra blank line under white-space:pre-wrap.
        const isNewlineChild = (child: unknown): boolean =>
          child === '\n' ||
          (typeof child === 'object' && child !== null && 'text' in child && (child as { text: string }).text === '\n');
        const hasOnlyNewlineChildren = children.length > 0 && children.every(isNewlineChild);
        const safeChildren = children.length > 0 && !hasOnlyNewlineChildren ? children : [{ text: '' }];

        return jsx('element', attrs, safeChildren) as unknown as Descendant;
      }
    }

    // Find mark rules — cumulative: multiple mark rules can apply to the same children
    const marks: Mark = {};
    for (const rule of rules) {
      if (rule.mark === undefined) {
        continue;
      }
      if (!ruleMatches(rule, element)) {
        continue;
      }

      const markValue: Mark =
        typeof rule.mark === 'function'
          ? rule.mark(element)
          : typeof rule.mark === 'string'
            ? { [rule.mark]: true }
            : rule.mark;

      Object.assign(marks, markValue);
    }

    if (Object.keys(marks).length > 0) {
      return children.reduce((accumulated: Array<DeserializedNode>, child) => {
        if (child === null) {
          return accumulated;
        }

        if (typeof child === 'object' && !Array.isArray(child)) {
          if ('type' in child) {
            // Element node — pass through; marks don't apply at block level
            accumulated.push(child as Descendant);
          } else {
            // Text leaf produced by a nested mark rule (e.g. <strong> inside <em>).
            // Merge the outer marks in rather than re-wrapping via jsx, which cannot
            // accept a plain object as a child and would throw.
            accumulated.push({ ...(child as Record<string, unknown>), ...marks } as Descendant);
          }
        } else {
          // String — wrap in a new text leaf carrying the marks
          accumulated.push(jsx('text', marks, child) as unknown as Descendant);
        }

        return accumulated;
      }, []);
    }

    // Unknown element — return children as a passthrough (transparent container)
    return children as Array<DeserializedNode>;
  };

  return deserializeNode;
};

const deserializeChildren = (
  element: HTMLElement,
  deserializeNode: (node: ChildNode) => DeserializedNode
): Array<unknown> =>
  Array.from(element.childNodes)
    .flatMap((child) => {
      const result = deserializeNode(child);

      if (result === null) {
        return [];
      }
      if (Array.isArray(result)) {
        return result;
      }

      return [result];
    })
    .filter((child) => child !== null && child !== undefined && child !== '');

// ---------------------------------------------------------------------------
// Public helper
// ---------------------------------------------------------------------------

/**
 * Runs the full HTML → Slate deserialization pipeline (parse, preprocess,
 * deserialize) and returns the resulting fragment as a flat array.
 * Primarily useful for testing; also available to consumers that want
 * deserialized nodes without inserting them into an editor.
 */
export const deserializeHtml = (htmlString: string, config: PasteHtmlConfig): Array<Descendant> => {
  const body = htmlStringToDomNode(htmlString);

  copyBlockMarksToSpanChild(body);
  const collapsedBody = collapseWhiteSpace(body);

  const deserializeNode = buildDeserializer(config);
  const fragment = deserializeNode(collapsedBody);

  if (fragment === null) {
    return [];
  }

  const nodes = Array.isArray(fragment) ? fragment : [fragment];

  return nodes.filter((node): node is Descendant => {
    if (node === null || node === undefined || node === '') {
      return false;
    }
    // Filter out top-level bare empty text nodes — they are not valid block
    // children and only appear when the source HTML was whitespace-only.
    if (typeof node === 'object' && 'text' in node && node.text === '') {
      return false;
    }

    return true;
  });
};

// ---------------------------------------------------------------------------
// Plugin
// ---------------------------------------------------------------------------

export const withPasteHtml =
  (config: PasteHtmlConfig): SlatePlugin =>
  <T extends Editor>(editor: T): T => {
    // `insertData` and `insertTextData` are added to the editor by slate-react's
    // ReactEditor. Cast through ClipboardEditor to access them without importing slate-react.
    const clipboardEditor = editor as unknown as ClipboardEditor;
    const originalInsertData = clipboardEditor.insertData.bind(clipboardEditor);

    clipboardEditor.insertData = (data: DataTransfer) => {
      // 1. Preserve native Slate-to-Slate copy behavior
      const slateFragment = data.getData('application/x-slate-fragment');

      if (slateFragment) {
        originalInsertData(data);

        return;
      }

      // 2. Deserialize text/html
      const htmlString = data.getData('text/html');

      if (htmlString) {
        try {
          const body = htmlStringToDomNode(htmlString);

          // Preprocessing passes
          copyBlockMarksToSpanChild(body);
          const collapsedBody = collapseWhiteSpace(body);

          const deserializeNode = buildDeserializer(config);
          const fragment = deserializeNode(collapsedBody);

          if (fragment !== null) {
            const nodes = Array.isArray(fragment) ? fragment : [fragment];
            const validNodes = nodes.filter(
              (node): node is Descendant => node !== null && node !== undefined && node !== ''
            );

            if (validNodes.length > 0) {
              Transforms.insertFragment(editor, validNodes);

              return;
            }
          }
        } catch {
          // Deserialization failed for this HTML — fall through to plain-text insertion.
        }
      }

      // 3. Fallback to plain-text insertion
      clipboardEditor.insertTextData(data);
    };

    return editor;
  };
